import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';

import { queryClient } from '@/adapters/query';
import { usersv2Client } from '@/adapters/schemaClients';
import { UserModel } from '@/models/UserModel';

import { ALL_FACILITIES } from './useFacilitiesQuery';

interface FetchAllUsersV2Params {
  facilityId?: null | string | string[] | typeof ALL_FACILITIES;
  statuses?: null | string | string[];
  requireCrmPermissions?: boolean;
  limit?: number;
  page?: number;
  name?: string;
}

interface FetchAllUsersV2Response {
  page: number;
  limit: number;
  has_next: boolean;
  total: number;
  users: UserModel[];
}

export const FIND_ALL_USERS_V2_QUERY_KEY = 'findAllUsersV2';
export const FIND_ALL_USERS_V2_DEFAULT_LIMIT = 50;

export const invalidateUsersFindAllV2Query = () => {
  queryClient.invalidateQueries([FIND_ALL_USERS_V2_QUERY_KEY]);
};

export const useUsersFindAllV2Query = () => {
  return {
    invalidateUsersFindAllV2Query,
    useFindAll: (
      params: FetchAllUsersV2Params,
      options: UseQueryOptions<FetchAllUsersV2Response, HTTPError> = {}
    ) =>
      useQuery<FetchAllUsersV2Response, HTTPError>(
        [FIND_ALL_USERS_V2_QUERY_KEY, params],
        async () => fetchAllUsersV2(params),
        {
          ...options
        }
      )
  };
};

async function fetchAllUsersV2({
  facilityId,
  limit,
  page,
  name,
  statuses
}: FetchAllUsersV2Params = {}): Promise<FetchAllUsersV2Response> {
  const getFacilityParams = () => {
    if (!facilityId || facilityId === ALL_FACILITIES) {
      return '';
    }

    const facilityIds = Array.isArray(facilityId) ? facilityId : [facilityId];
    return facilityIds.join(',');
  };

  const getStatusParams = () => {
    if (!statuses) {
      return undefined;
    }

    const statusList = Array.isArray(statuses) ? statuses : [statuses];
    return statusList.join(',');
  };

  const response = await usersv2Client.findAllUsersV2({
    page,
    limit,
    facilityIds: getFacilityParams(),
    statuses: getStatusParams(),
    name
  });

  return {
    page: response.data?.page || 0,
    limit: response.data?.limit || 0,
    has_next: response.data?.has_next || false,
    total: response.data?.total || 0,
    users: response.data?.data.map((user) => new UserModel(user as any)) || []
  };
}
